import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Edit, Trash2, Package, Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProductsManagement = ({ products, onRefresh }) => {
  const { toast } = useToast();
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'ARS',
    category: 'Componentes',
    image: '',
    images: [],
    stock_quantity: 0,
    discount: 0
  });
  const token = localStorage.getItem('krolbolt_token');

  const handleFileUpload = async (file) => {
    if (!file) return null;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API}/upload/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        return data.url;
      } else {
        throw new Error('Error uploading file');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({ title: 'Error', description: 'No se pudo subir el archivo', variant: 'destructive' });
      return null;
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    
    if (!newProduct.image) {
      toast({ title: 'Error', description: 'Debes subir una imagen del producto', variant: 'destructive' });
      return;
    }

    try {
      const response = await fetch(`${API}/products`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newProduct,
          price: parseFloat(newProduct.price),
          stock_quantity: parseInt(newProduct.stock_quantity) || 0,
          discount: parseInt(newProduct.discount) || 0
        })
      });

      if (!response.ok) throw new Error('Error creando producto');
      
      toast({ title: 'Éxito', description: 'Producto creado correctamente' });
      setNewProduct({
        name: '',
        description: '',
        price: '',
        currency: 'ARS',
        category: 'Componentes',
        image: '',
        images: [],
        stock_quantity: 0,
        discount: 0
      });
      onRefresh();
    } catch (error) {
      console.error('Error:', error);
      toast({ title: 'Error', description: 'Error al crear producto', variant: 'destructive' });
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/products/${editingProduct.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...editingProduct,
          price: parseFloat(editingProduct.price),
          stock_quantity: parseInt(editingProduct.stock_quantity) || 0,
          discount: parseInt(editingProduct.discount) || 0
        })
      });

      if (!response.ok) throw new Error('Error actualizando producto');
      
      toast({ title: 'Éxito', description: 'Producto actualizado correctamente' });
      setEditingProduct(null);
      onRefresh();
    } catch (error) {
      console.error('Error:', error);
      toast({ title: 'Error', description: 'Error al actualizar producto', variant: 'destructive' });
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;
    
    try {
      const response = await fetch(`${API}/products/${productId}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Error eliminando producto');
      toast({ title: 'Éxito', description: 'Producto eliminado correctamente' });
      onRefresh();
    } catch (error) {
      console.error('Error:', error);
      toast({ title: 'Error', description: 'Error al eliminar producto', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      {!editingProduct && (
        <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Plus className="h-5 w-5" />Agregar Nuevo Producto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateProduct} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Nombre *</Label>
                  <Input
                    required
                    value={newProduct.name}
                    onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                    className="bg-slate-900/50 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Categoría</Label>
                  <Select value={newProduct.category} onValueChange={(value) => setNewProduct({...newProduct, category: value})}>
                    <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Componentes">Componentes</SelectItem>
                      <SelectItem value="Accesorios">Accesorios</SelectItem>
                      <SelectItem value="Periféricos">Periféricos</SelectItem>
                      <SelectItem value="Equipos Nuevos">Equipos Nuevos</SelectItem>
                      <SelectItem value="Equipos Usados">Equipos Usados</SelectItem>
                      <SelectItem value="Otros">Otros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Precio *</Label>
                  <Input
                    required
                    type="number"
                    step="0.01"
                    value={newProduct.price}
                    onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                    className="bg-slate-900/50 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Moneda</Label>
                  <Select value={newProduct.currency} onValueChange={(value) => setNewProduct({...newProduct, currency: value})}>
                    <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ARS">ARS</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Stock</Label>
                  <Input
                    type="number"
                    value={newProduct.stock_quantity}
                    onChange={(e) => setNewProduct({...newProduct, stock_quantity: e.target.value})}
                    className="bg-slate-900/50 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Descuento (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={newProduct.discount}
                    onChange={(e) => setNewProduct({...newProduct, discount: e.target.value})}
                    className="bg-slate-900/50 border-slate-700 text-white"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Descripción</Label>
                <Textarea
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                  className="bg-slate-900/50 border-slate-700 text-white"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Imagen del Producto (JPG, PNG) *</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setUploadingImage(true);
                        const url = await handleFileUpload(file);
                        if (url) {
                          setNewProduct({...newProduct, image: url});
                          toast({ title: 'Imagen subida exitosamente' });
                        }
                        setUploadingImage(false);
                      }
                    }}
                    className="bg-slate-900/50 border-slate-700 text-white"
                    disabled={uploadingImage}
                    required
                  />
                  {uploadingImage && <Loader2 className="h-4 w-4 animate-spin text-cyan-400" />}
                  {newProduct.image && <Badge className="bg-green-500/20 text-green-400">✓ Subida</Badge>}
                </div>
                {newProduct.image && (
                  <img src={newProduct.image} alt="Preview" className="mt-2 h-32 w-32 object-cover rounded" />
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Imágenes Adicionales (hasta 6 total)</Label>
                <p className="text-slate-500 text-xs mb-2">Agregadas: {newProduct.images.length}/6</p>
                {newProduct.images.length < 6 && (
                  <div className="flex gap-2 items-center">
                    <Input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file && newProduct.images.length < 6) {
                          setUploadingImage(true);
                          const url = await handleFileUpload(file);
                          if (url) {
                            setNewProduct({...newProduct, images: [...newProduct.images, url]});
                            toast({ title: `Imagen ${newProduct.images.length + 1} subida` });
                          }
                          setUploadingImage(false);
                        }
                      }}
                      className="bg-slate-900/50 border-slate-700 text-white"
                      disabled={uploadingImage}
                    />
                    {uploadingImage && <Loader2 className="h-4 w-4 animate-spin text-cyan-400" />}
                  </div>
                )}
                {newProduct.images.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {newProduct.images.map((img, idx) => (
                      <div key={idx} className="relative">
                        <img src={img} alt={`Adicional ${idx + 1}`} className="w-full h-24 object-cover rounded" />
                        <button
                          type="button"
                          onClick={() => {
                            const newImages = newProduct.images.filter((_, i) => i !== idx);
                            setNewProduct({...newProduct, images: newImages});
                          }}
                          className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 text-xs hover:bg-red-700"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button type="submit" disabled={uploadingImage || !newProduct.image} className="w-full bg-gradient-to-r from-cyan-600 to-emerald-600">
                <Plus className="h-4 w-4 mr-2" />Crear Producto
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <h2 className="text-2xl font-bold text-white flex items-center gap-2">
        <Package className="h-6 w-6" />Gestión de Productos
      </h2>

      {editingProduct && (
        <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Editar Producto</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateProduct} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Nombre *</Label>
                  <Input
                    required
                    value={editingProduct.name}
                    onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})}
                    className="bg-slate-900/50 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Categoría</Label>
                  <Select value={editingProduct.category} onValueChange={(value) => setEditingProduct({...editingProduct, category: value})}>
                    <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Componentes">Componentes</SelectItem>
                      <SelectItem value="Accesorios">Accesorios</SelectItem>
                      <SelectItem value="Periféricos">Periféricos</SelectItem>
                      <SelectItem value="Equipos Nuevos">Equipos Nuevos</SelectItem>
                      <SelectItem value="Equipos Usados">Equipos Usados</SelectItem>
                      <SelectItem value="Otros">Otros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Precio *</Label>
                  <Input
                    required
                    type="number"
                    step="0.01"
                    value={editingProduct.price}
                    onChange={(e) => setEditingProduct({...editingProduct, price: e.target.value})}
                    className="bg-slate-900/50 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Moneda</Label>
                  <Select value={editingProduct.currency} onValueChange={(value) => setEditingProduct({...editingProduct, currency: value})}>
                    <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ARS">ARS</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Stock</Label>
                  <Input
                    type="number"
                    value={editingProduct.stock_quantity}
                    onChange={(e) => setEditingProduct({...editingProduct, stock_quantity: e.target.value})}
                    className="bg-slate-900/50 border-slate-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Descuento (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={editingProduct.discount}
                    onChange={(e) => setEditingProduct({...editingProduct, discount: e.target.value})}
                    className="bg-slate-900/50 border-slate-700 text-white"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Descripción</Label>
                <Textarea
                  value={editingProduct.description}
                  onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
                  className="bg-slate-900/50 border-slate-700 text-white"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Actualizar Imagen (opcional)</Label>
                <div className="flex gap-2 items-center">
                  <Input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setUploadingImage(true);
                        const url = await handleFileUpload(file);
                        if (url) {
                          setEditingProduct({...editingProduct, image: url});
                          toast({ title: 'Imagen subida exitosamente' });
                        }
                        setUploadingImage(false);
                      }
                    }}
                    className="bg-slate-900/50 border-slate-700 text-white"
                    disabled={uploadingImage}
                  />
                  {uploadingImage && <Loader2 className="h-4 w-4 animate-spin text-cyan-400" />}
                </div>
                {editingProduct.image && (
                  <img src={editingProduct.image} alt="Preview" className="mt-2 h-32 w-32 object-cover rounded" />
                )}
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1 bg-gradient-to-r from-cyan-600 to-emerald-600">
                  <Edit className="h-4 w-4 mr-2" />Guardar Cambios
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setEditingProduct(null)}
                  className="flex-1 border-slate-700 text-slate-300"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((product) => (
          <Card key={product.id} className="bg-slate-800/50 backdrop-blur-sm border-slate-700 overflow-hidden">
            <div className="relative">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-48 object-cover"
              />
              {product.discount > 0 && (
                <Badge className="absolute top-2 right-2 bg-red-600">
                  -{product.discount}% OFF
                </Badge>
              )}
              <Badge className="absolute top-2 left-2 bg-cyan-600">
                {product.currency}
              </Badge>
            </div>
            <CardContent className="p-4 space-y-2">
              <h3 className="text-white font-bold text-lg">{product.name}</h3>
              <p className="text-slate-400 text-sm">{product.category}</p>
              <div className="flex justify-between items-center">
                <span className="text-cyan-400 font-bold text-xl">
                  ${product.price.toLocaleString()} {product.currency}
                </span>
                {product.stock_quantity !== undefined && (
                  <Badge className={product.stock_quantity > 0 ? "bg-green-600" : "bg-red-600"}>
                    {product.stock_quantity > 0 ? "En Stock" : "Sin Stock"}
                  </Badge>
                )}
              </div>
              <div className="flex gap-2 pt-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => setEditingProduct(product)}
                  className="flex-1 border-cyan-600 text-cyan-400 hover:bg-cyan-600/10"
                >
                  <Edit className="h-4 w-4 mr-1" />Editar
                </Button>
                <Button 
                  size="sm" 
                  variant="destructive"
                  onClick={() => handleDeleteProduct(product.id)}
                  className="flex-1"
                >
                  <Trash2 className="h-4 w-4 mr-1" />Eliminar
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ProductsManagement;
